import { Button, ConfigProvider, DatePicker, Input, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useMemo } from 'react';
import {
  useGetStudySitesOptionsQuery,
  useGetStudyModalitiesOptionsQuery,
} from '@/state/slices/options';
import {
  useGetReferringPhysiciansQuery,
  useGetSonographersQuery,
} from '@/state/slices/reports';
import type { DatePreset } from '@/models/report';
import { CollapsibleSection } from '@/shared/ui/CollapsibleSection';
import styles from './PatientFilterRail.module.css';

interface PatientFilterRailProps {
  searchText: string;
  onSearchChange: (text: string) => void;
  selectedSites: string[];
  onSitesChange: (sites: string[]) => void;
  selectedStudyTypes: string[];
  onStudyTypesChange: (types: string[]) => void;
  datePreset: DatePreset | null;
  onDatePresetChange: (preset: DatePreset | null) => void;
  customDateRange: [string, string] | null;
  onCustomDateRangeChange: (range: [string, string] | null) => void;
  selectedReferringPhysicians: string[];
  onReferringPhysiciansChange: (v: string[]) => void;
  selectedSonographers: string[];
  onSonographersChange: (v: string[]) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

const STUDY_TYPE_OPTIONS = [
  { label: 'TTE', value: 'tte' },
  { label: 'Stress Echo', value: 'stress_echo' },
  { label: 'Other', value: 'other' },
];

const DATE_PRESETS: { label: string; value: DatePreset }[] = [
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: 'last7' },
  { label: 'Last 30 days', value: 'last30' },
  { label: 'Custom', value: 'custom' },
];

const filterTheme = {
  components: {
    Input: { colorBgContainer: 'var(--sub-sidebar-surface-1)' },
    Select: { colorBgContainer: 'var(--sub-sidebar-surface-1)' },
    DatePicker: { colorBgContainer: 'var(--sub-sidebar-surface-1)' },
  },
};

export default function PatientFilterRail({
  searchText,
  onSearchChange,
  selectedSites,
  onSitesChange,
  selectedStudyTypes,
  onStudyTypesChange,
  datePreset,
  onDatePresetChange,
  customDateRange,
  onCustomDateRangeChange,
  selectedReferringPhysicians,
  onReferringPhysiciansChange,
  selectedSonographers,
  onSonographersChange,
  hasActiveFilters,
  onClearFilters,
}: PatientFilterRailProps) {
  const { data: sites } = useGetStudySitesOptionsQuery();
  const { data: modalities } = useGetStudyModalitiesOptionsQuery();
  const { data: referringData } = useGetReferringPhysiciansQuery();
  const { data: sonographerData } = useGetSonographersQuery();

  const referringOptions = useMemo(
    () =>
      referringData?.results.map((p) => ({
        value: p.uuid,
        label: `${p.firstName} ${p.lastName}`,
      })) ?? [],
    [referringData]
  );

  const sonographerOptions = useMemo(
    () =>
      sonographerData?.results.map((p) => ({
        value: p.uuid,
        label: `${p.firstName} ${p.lastName}`,
      })) ?? [],
    [sonographerData]
  );

  const studyTypeOptions = useMemo(
    () =>
      modalities
        ? modalities.map((m) => ({ value: m, label: m }))
        : STUDY_TYPE_OPTIONS,
    [modalities]
  );

  const scopeCount = selectedSites.length;
  const personnelCount =
    selectedReferringPhysicians.length + selectedSonographers.length;
  const dateActive = datePreset !== null;
  const studyDetailsCount = selectedStudyTypes.length + (dateActive ? 1 : 0);

  const handleRangePicker = (
    _dates: [Dayjs | null, Dayjs | null] | null,
    dateStrings: [string, string]
  ) => {
    if (dateStrings[0] && dateStrings[1]) {
      onCustomDateRangeChange(dateStrings);
    } else {
      onCustomDateRangeChange(null);
    }
  };

  const clearPersonnel = () => {
    onReferringPhysiciansChange([]);
    onSonographersChange([]);
  };

  const clearStudyDetails = () => {
    onDatePresetChange(null);
    onCustomDateRangeChange(null);
    onStudyTypesChange([]);
  };

  return (
    <ConfigProvider theme={filterTheme}>
      <div className={styles.filterRail}>
        {/* ── Search (always visible) ──────────── */}
        <div className={styles.section}>
          <div className={styles.filterGroup}>
            <label htmlFor="patient-search" className={styles.filterLabel}>
              Search
            </label>
            <Input
              id="patient-search"
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search patient, MRN..."
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>

        {/* ── Scope (Site / Org) ───────────────── */}
        <CollapsibleSection
          title="Scope"
          count={scopeCount}
          onClear={() => onSitesChange([])}
        >
          <div className={styles.filterGroup}>
            <label htmlFor="patient-site" className={styles.filterLabel}>
              Site / Org
            </label>
            <Select
              id="patient-site"
              mode="multiple"
              allowClear
              placeholder="All sites"
              value={selectedSites}
              onChange={onSitesChange}
              options={sites?.map((s) => ({ value: s, label: s })) ?? []}
              className={styles.fullWidth}
            />
          </div>
        </CollapsibleSection>

        {/* ── Personnel ────────────────────────── */}
        <CollapsibleSection
          title="Personnel"
          count={personnelCount}
          onClear={clearPersonnel}
        >
          <div className={styles.filterGroup}>
            <label htmlFor="patient-referring" className={styles.filterLabel}>
              Referring Physician
            </label>
            <Select
              id="patient-referring"
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="All"
              value={selectedReferringPhysicians}
              onChange={onReferringPhysiciansChange}
              options={referringOptions}
              className={styles.fullWidth}
            />
          </div>
          <div className={styles.filterGroup}>
            <label htmlFor="patient-sonographer" className={styles.filterLabel}>
              Sonographer / Tech
            </label>
            <Select
              id="patient-sonographer"
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="All"
              value={selectedSonographers}
              onChange={onSonographersChange}
              options={sonographerOptions}
              className={styles.fullWidth}
            />
          </div>
        </CollapsibleSection>

        {/* ── Study Details (Date + Study type) ── */}
        <CollapsibleSection
          title="Study Details"
          count={studyDetailsCount}
          onClear={clearStudyDetails}
        >
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Study date</span>
            <div className={styles.datePresetGrid}>
              {DATE_PRESETS.map(({ label, value }) => (
                <Button
                  key={value}
                  size="small"
                  type={datePreset === value ? 'primary' : 'default'}
                  className={styles.datePresetBtn}
                  onClick={() =>
                    onDatePresetChange(datePreset === value ? null : value)
                  }
                >
                  {label}
                </Button>
              ))}
            </div>
            {datePreset === 'custom' && (
              <DatePicker.RangePicker
                className={styles.fullWidth}
                value={
                  customDateRange
                    ? [dayjs(customDateRange[0]), dayjs(customDateRange[1])]
                    : null
                }
                onChange={handleRangePicker}
              />
            )}
          </div>
          <div className={styles.filterGroup}>
            <label htmlFor="patient-study-type" className={styles.filterLabel}>
              Study type
            </label>
            <Select
              id="patient-study-type"
              mode="multiple"
              allowClear
              placeholder="All types"
              value={selectedStudyTypes}
              onChange={onStudyTypesChange}
              options={studyTypeOptions}
              className={styles.fullWidth}
            />
          </div>
        </CollapsibleSection>

        {/* ── Footer ───────────────────────────── */}
        {hasActiveFilters && (
          <div className={styles.footer}>
            <Button
              block
              className={styles.footerButton}
              onClick={onClearFilters}
            >
              Clear All Filters
            </Button>
          </div>
        )}
      </div>
    </ConfigProvider>
  );
}
